/**
 * Pure mappers for the inventory detail view: `Item` → Overview info rows and
 * activity logs → Timeline items. Kept separate so the view component stays
 * presentation-only. Plain-language labels for sales/ops staff.
 */
import type { InfoItem } from '@/pages/account/components/InfoGrid';
import type { TimelineItem, TimelineTone } from '@vnetwork/ui/data';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import type { Item, WarehouseActivityLog } from '@/api/capacities';
import { formatWhDateTime } from '../shared/wh-format';
import { itemStatusCell } from './items-status';

/** Percent value → "12.5%" (depreciation rate). */
const percent = (n?: number) => (n != null ? `${n}%` : undefined);

/**
 * Equipment / Component "Information" card rows, matched 1-1 to the legacy
 * warehouse render: identity + placement + purchase/warranty + audit dates
 * (en-US). Empty optional fields are omitted (legacy hides them). `noun` sets the
 * name label and which reference field (service tag vs ref) leads.
 */
export function itemInfoItems(item: Item, noun: 'Equipment' | 'Component'): InfoItem[] {
  const x = item as Item & {
    serial_number?: string;
    description?: string;
    notes?: string;
    rack?: { name?: string; code?: string };
  };
  const rows: InfoItem[] = [{ label: `${noun} Name`, value: item.name || '—' }];
  if (noun === 'Equipment') rows.push({ label: 'Service Tag', value: item.service_tag || '—' });
  if (x.serial_number) rows.push({ label: 'Serial Number', value: x.serial_number });
  if (item.ref) rows.push({ label: 'Ref', value: item.ref });
  rows.push({ label: 'Category', value: item.category || '—' });
  rows.push({ label: 'Status', value: itemStatusCell(item.status) });
  rows.push({ label: 'Condition', value: item.condition || '—' });
  if (item.current_location?.name) rows.push({ label: 'Location', value: item.current_location.name });
  if (x.rack?.name || x.rack?.code) rows.push({ label: 'Rack', value: x.rack.name || x.rack.code });
  rows.push({ label: 'Position', value: item.rack_position_u ? `U${item.rack_position_u}` : '—' });
  if (item.model?.name) rows.push({ label: 'Model', value: item.model.name });
  if (item.supplier?.name) rows.push({ label: 'Supplier', value: item.supplier.name });
  if (item.purchase_date) rows.push({ label: 'Purchase Date', value: formatWhDateTime(item.purchase_date) });
  if (item.purchase_price)
    rows.push({
      label: 'Purchase Price',
      value: `${item.purchase_price.toLocaleString()} ${item.purchase_currency || 'VND'}`,
    });
  if (item.owner) rows.push({ label: 'Owner', value: item.owner });
  if (x.description) rows.push({ label: 'Description', value: x.description, full: true });
  if (x.notes) rows.push({ label: 'Notes', value: x.notes, full: true });
  rows.push({ label: 'Created At', value: formatWhDateTime(item.created_at) });
  rows.push({ label: 'Updated At', value: formatWhDateTime(item.updated_at) });
  return rows;
}

/** Build the Overview info-card rows from an inventory `Item`. */
export function overviewItems(item: Item): InfoItem[] {
  const brandModel = [item.model?.model, item.model?.name].filter(Boolean).join(' · ');
  return [
    { label: 'Category', value: item.category },
    { label: 'Model', value: brandModel || item.model?.name },
    { label: 'Condition', value: item.condition },
    { label: 'Owner', value: item.owner },
    { label: 'Supplier', value: item.supplier?.name },
    { label: 'Location', value: item.current_location?.name },
    {
      label: 'Quantity',
      value: item.quantity != null ? `${item.quantity} ${item.unit ?? ''}`.trim() : undefined,
    },
    { label: 'Purchase Date', value: formatDate(item.purchase_date) },
    {
      label: 'Purchase Price',
      value: item.purchase_price != null ? formatMoney(item.purchase_price) : undefined,
    },
    { label: 'Warranty Start', value: formatDate(item.warranty_start_date) },
    { label: 'Warranty End', value: formatDate(item.warranty_end_date) },
    { label: 'Depreciation Rate', value: percent(item.depreciation_rate) },
    {
      label: 'Current Value',
      value: item.current_value != null ? formatMoney(item.current_value) : undefined,
    },
  ];
}

/** Pick a Timeline tone from a log action verb (best-effort, plain-language). */
function toneForAction(action?: string): TimelineTone {
  const a = (action ?? '').toLowerCase();
  if (/(delete|remove|damage|reject|cancel|fail)/.test(a)) return 'danger';
  if (/(loan|return|transfer|move|warn)/.test(a)) return 'warning';
  if (/(create|add|import|approve|complete|assign)/.test(a)) return 'success';
  if (/(update|edit|change)/.test(a)) return 'info';
  return 'neutral';
}

/** Map warehouse activity logs to Timeline items (newest first, as returned). */
export function logsToTimeline(logs: WarehouseActivityLog[]): TimelineItem[] {
  return logs.map((log) => ({
    title: log.action || 'Activity',
    meta: log.employee_name,
    description: log.description,
    timestamp: log.created_at ? formatDateTime(log.created_at) : undefined,
    tone: toneForAction(log.action),
  }));
}
